import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { BarChart } from "../../components/Charts";
import { Alert, Badge, Card, Loading, PageHeader, Stat } from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { ago, brl, percent } from "../../lib/format";

interface Dashboard {
  leads: { perDay: { date: string; source: string; count: number }[]; total30d: number };
  sellers: {
    sellerId: string | null;
    name: string;
    leads: number;
    contacted: number;
    won: number;
    closeRate: number;
    staleNew: number;
    avgFirstContactMinutes: number | null;
  }[];
  enrollments: Record<string, number>;
  mrrCents: number;
  delinquency: { state: string; enrollments: number; openCents: number }[];
  ai: {
    sessions: number;
    minutes: number;
    costUsd: number;
    p50Ms: number | null;
    p95Ms: number | null;
  };
  teachers: {
    teacherId: string;
    name: string;
    status: string;
    availableMinutes: number;
    occupiedMinutes: number;
    occupancy: number;
    activeStudents: number;
    maxStudents: number;
    walletApproved: boolean;
    churn30d: number;
  }[];
  withoutFirstContact: {
    studentId: string;
    name: string;
    teacherName: string;
    activatedAt: string;
  }[];
  errors24h: {
    webhooksFailed: number;
    emailsFailed: number;
    jobsFailed: { name: string; n: number }[];
    flaggedSessions: number;
  };
}

export function AdminDashboard() {
  const q = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get<Dashboard>("/v1/admin/metrics"),
    refetchInterval: 60_000,
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <Alert kind="risk">{errorMessage(q.error)}</Alert>;
  const d = q.data;
  const byDay = new Map<string, number>();
  const bySource = new Map<string, number>();
  for (const l of d.leads.perDay) {
    byDay.set(l.date, (byDay.get(l.date) ?? 0) + l.count);
    bySource.set(l.source, (bySource.get(l.source) ?? 0) + l.count);
  }
  const active = d.enrollments.active ?? 0;
  const overdue = d.delinquency.find((x) => x.state === "overdue");
  const suspended = d.delinquency.find((x) => x.state === "suspended");
  const jobsFailed = d.errors24h.jobsFailed.reduce((a, j) => a + j.n, 0);
  const errors = d.errors24h.webhooksFailed + d.errors24h.emailsFailed + jobsFailed;

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Últimos 30 dias de leads e a situação de hoje." />
      <div className="grid cols-4 keep-2">
        <Stat label="MRR" value={brl(d.mrrCents)} hint={`${active} matrículas ativas`} />
        <Stat
          label="Inadimplência"
          value={brl((overdue?.openCents ?? 0) + (suspended?.openCents ?? 0))}
          hint={`${overdue?.enrollments ?? 0} em atraso, ${suspended?.enrollments ?? 0} suspensas`}
        />
        <Stat
          label="IA no mês"
          value={`${d.ai.minutes} min`}
          hint={`US$ ${d.ai.costUsd.toFixed(2)} · p50 ${d.ai.p50Ms ?? "..."} ms · p95 ${d.ai.p95Ms ?? "..."} ms`}
        />
        <Stat
          label="Erros nas últimas 24 h"
          value={errors}
          hint={`${d.errors24h.flaggedSessions} sessões marcadas`}
        />
      </div>
      {errors > 0 || d.errors24h.flaggedSessions > 0 ? (
        <div style={{ marginTop: 12 }}>
          <Alert kind="warning">
            Confira em <Link to="/admin/logs">Logs</Link>: {d.errors24h.webhooksFailed} webhooks,{" "}
            {d.errors24h.emailsFailed} e-mails e {jobsFailed} jobs com falha.
          </Alert>
        </div>
      ) : null}
      <div className="grid cols-2" style={{ marginTop: 16 }}>
        <Card title={`Leads por dia (${d.leads.total30d})`}>
          <BarChart
            data={[...byDay.entries()].map(([date, value]) => ({
              label: date.slice(8, 10),
              value,
            }))}
          />
          <div className="row small" style={{ marginTop: 8 }}>
            {[...bySource.entries()].map(([s, n]) => (
              <Badge key={s} color="blue">
                {s}: {n}
              </Badge>
            ))}
          </div>
        </Card>
        <Card title="Vendedoras">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Vendedora</th>
                  <th className="num">Leads</th>
                  <th className="num">Fechamento</th>
                  <th className="num">1º contato</th>
                  <th className="num">Novos +24 h</th>
                </tr>
              </thead>
              <tbody>
                {d.sellers.map((s) => (
                  <tr key={s.sellerId ?? "none"}>
                    <td>{s.name}</td>
                    <td className="num">{s.leads}</td>
                    <td className="num">{percent(s.closeRate)}</td>
                    <td className="num">
                      {s.avgFirstContactMinutes === null
                        ? "..."
                        : `${Math.round(s.avgFirstContactMinutes)} min`}
                      {s.avgFirstContactMinutes !== null && s.avgFirstContactMinutes > 60
                        ? " (contratar)"
                        : ""}
                    </td>
                    <td className="num">{s.staleNew}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="small muted" style={{ marginTop: 8 }}>
            Regra: contratar a próxima vendedora quando o primeiro contato passar de 1 hora ou
            houver leads sem resposta há 24 h.
          </p>
        </Card>
      </div>
      <div className="grid cols-2" style={{ marginTop: 16 }}>
        <Card title="Ocupação por professor">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Professor</th>
                  <th className="num">Ocupação</th>
                  <th className="num">Alunos</th>
                  <th className="num">Churn 30d</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {d.teachers.map((t) => (
                  <tr key={t.teacherId}>
                    <td>{t.name}</td>
                    <td className="num">
                      {percent(t.occupancy)}{" "}
                      <span className="muted small">
                        ({Math.round(t.occupiedMinutes / 60)}h de{" "}
                        {Math.round(t.availableMinutes / 60)}h)
                      </span>
                    </td>
                    <td className="num">
                      {t.activeStudents}/{t.maxStudents}
                    </td>
                    <td className="num">{percent(t.churn30d)}</td>
                    <td>
                      {t.walletApproved ? (
                        <Badge color="green">{t.status}</Badge>
                      ) : (
                        <Badge color="gold">subconta pendente</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card title="Alunos sem primeiro contato há 72 h">
          {d.withoutFirstContact.length === 0 ? (
            <p className="muted">Todos os alunos novos já foram contatados.</p>
          ) : null}
          {d.withoutFirstContact.map((s) => (
            <div key={s.studentId} className="row between" style={{ minHeight: 44 }}>
              <Link to={`/admin/alunos/${s.studentId}`}>{s.name}</Link>
              <span className="small muted">
                {s.teacherName} · ativo {ago(s.activatedAt)}
              </span>
            </div>
          ))}
        </Card>
      </div>
    </>
  );
}
